import {
	EventDelta,
	MarketDelta,
	SelectionDelta,
	SportPreferredMarketTypeDto,
	StreamMessagePayloadDto,
	StreamMessagePayloadEventRemovedDto,
	StreamMessagePayloadEventUpdatedDto,
	StreamMessagePayloadInitialStateDto,
	StreamMessagePayloadMarketRemovedDto,
	StreamMessagePayloadMarketUpdatedDto,
	StreamMessagePayloadSelectionRemovedDto,
	StreamMessagePayloadSelectionUpdatedDto,
	StreamMessageTypeDto
} from './sbapi/sbapi-api-model';

type StreamMessageTemplate<T extends StreamMessageTypeDto, P extends StreamMessagePayloadDto> = {
	type: T;
	payload: P;
};

export type StreamMessageInitialState = StreamMessageTemplate<
	StreamMessageTypeDto.INITIAL_STATE,
	StreamMessagePayloadInitialStateDto
>;

export type StreamMessageEventAdded = StreamMessageTemplate<
	StreamMessageTypeDto.EVENT_ADDED,
	StreamMessagePayloadEventUpdatedDto
>;

export type StreamMessageMarketAdded = StreamMessageTemplate<
	StreamMessageTypeDto.MARKET_ADDED,
	StreamMessagePayloadMarketUpdatedDto
>;

export type StreamMessageSelectionAdded = StreamMessageTemplate<
	StreamMessageTypeDto.SELECTION_ADDED,
	StreamMessagePayloadSelectionUpdatedDto
>;

export type StreamMessageEventRemoved = StreamMessageTemplate<
	StreamMessageTypeDto.EVENT_REMOVED,
	StreamMessagePayloadEventRemovedDto
>;

export type StreamMessageMarketRemoved = StreamMessageTemplate<
	StreamMessageTypeDto.MARKET_REMOVED,
	StreamMessagePayloadMarketRemovedDto
>;

export type StreamMessageSelectionRemoved = StreamMessageTemplate<
	StreamMessageTypeDto.SELECTION_REMOVED,
	StreamMessagePayloadSelectionRemovedDto
>;

export type StreamMessageEventDelta = StreamMessageTemplate<StreamMessageTypeDto.EVENT_DELTA, { delta: EventDelta }>;

export type StreamMessageMarketDelta = StreamMessageTemplate<StreamMessageTypeDto.MARKET_DELTA, { delta: MarketDelta }>;

export type StreamMessageSelectionDelta = StreamMessageTemplate<
	StreamMessageTypeDto.SELECTION_DELTA,
	{ delta: SelectionDelta }
>;

export type StreamMessage =
	| StreamMessageInitialState
	| StreamMessageEventAdded
	| StreamMessageMarketAdded
	| StreamMessageSelectionAdded
	| StreamMessageEventRemoved
	| StreamMessageMarketRemoved
	| StreamMessageSelectionRemoved
	| StreamMessageEventDelta
	| StreamMessageMarketDelta
	| StreamMessageSelectionDelta;

export type SubscriptionFilters = {
	sportIds?: string[] | null;
	leagueIds?: string[] | null;
	eventIds?: string[] | null;
	eventTypes?: string[] | null;
	marketIds?: string[] | null;
	marketNames?: string[] | null;
	marketTypeIds?: string[] | null;
	selectionIds?: string[] | null;
	isLive?: boolean;
	eventStartDateFrom?: string | null;
	eventStartDateTo?: string | null;
	points?: number | null;
	isPreferredMarket?: boolean | null;
	marketTypeFilters?: Array<
		Pick<SportPreferredMarketTypeDto, 'marketTypeIds' | 'points' | 'isPreferredMarket'>
	> | null;
};

export enum EventSortOrder {
	SPORT_ORDER_ASC, // Sort by sport order in ascending order.
	LEAGUE_ORDER_ASC, // Sort by league order in ascending order.
	START_DATE_ASC, // Sort by start date in ascending order.
	NONE, // No sorting.
	PRESERVE_ORDER // Preserve the order specified in SubscriptionFilters.eventIds array, as fallback BE will use NONE.
}

export type Subscription = {
	ref: string;
	filters: SubscriptionFilters;
	orderBy?: EventSortOrder;
	page?: number;
	pageSize?: number;
	onSubscribed: () => void;
	onMessage: (messages: StreamMessage[]) => void;
	onError: (message: string, error: Error) => void;
	onMaxResubscribeAttemptsReached?: (attempts: number, error?: Error) => void;
	cancel?: () => void;
	request?: (n: number) => void;
	pendingRequestCount?: number;
};

export const isMessageInitialState = (message: StreamMessage): message is StreamMessageInitialState => {
	return message.type === StreamMessageTypeDto.INITIAL_STATE;
};

export const isMessageEventAdded = (message: StreamMessage): message is StreamMessageEventAdded => {
	return message.type === StreamMessageTypeDto.EVENT_ADDED;
};

export const isMessageMarketAdded = (message: StreamMessage): message is StreamMessageMarketAdded => {
	return message.type === StreamMessageTypeDto.MARKET_ADDED;
};

export const isMessageSelectionAdded = (message: StreamMessage): message is StreamMessageSelectionAdded => {
	return message.type === StreamMessageTypeDto.SELECTION_ADDED;
};

export const isMessageEventRemoved = (message: StreamMessage): message is StreamMessageEventRemoved => {
	return message.type === StreamMessageTypeDto.EVENT_REMOVED;
};

export const isMessageMarketRemoved = (message: StreamMessage): message is StreamMessageMarketRemoved => {
	return message.type === StreamMessageTypeDto.MARKET_REMOVED;
};

export const isMessageSelectionRemoved = (message: StreamMessage): message is StreamMessageSelectionRemoved => {
	return message.type === StreamMessageTypeDto.SELECTION_REMOVED;
};

export const isMessageEventDelta = (message: StreamMessage): message is StreamMessageEventDelta => {
	return message.type === StreamMessageTypeDto.EVENT_DELTA;
};

export const isMessageMarketDelta = (message: StreamMessage): message is StreamMessageMarketDelta => {
	return message.type === StreamMessageTypeDto.MARKET_DELTA;
};

export const isMessageSelectionDelta = (message: StreamMessage): message is StreamMessageSelectionDelta => {
	return message.type === StreamMessageTypeDto.SELECTION_DELTA;
};
